/**
 * 55 — screen 3: THE UPLOAD, as a confirm flow.
 *
 * Record or pick a file → the bytes go straight to R2 → the moment it lands we
 * TRANSCRIBE it and GUESS which song it is → you get a "confirm this" step,
 * pre-filled: the song we think it is, the words we heard, and a star toggle for
 * "make this the main take". Confirm files it live; Cancel scraps it.
 *
 * Two things are load-bearing and unchanged:
 *  1. /api/uploads/presign is the money-safety valve (ADR-002) — it refuses a
 *     file that's too big, or a phone pushing a .wav master, BEFORE a byte moves.
 *  2. The bytes never touch the Worker: browser → R2 over a presigned PUT.
 *
 * What changed: we no longer ask "which song is this?" up front (it guessed
 * wrong to a human because nothing landed to see). We land it, read it, and let
 * you confirm — which is the honest order.
 */

import Uppy, { type UppyFile } from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import {
  presignUpload,
  completeUpload,
  analyzeTake,
  confirmTake,
  createSong,
  setCanonical,
  putSongLyrics,
  scrapTake,
  getSongs,
  getSongLyrics,
  ApiError,
  type PresignResponse,
  type Song,
  type AnalyzeResult,
} from './api';
import { h, clear, toast, fmtTime } from './ui';
import type { Screen } from './shelf';

type Meta = Record<string, unknown>;
type Body = Record<string, unknown>;

type ItemState =
  | 'checking'
  | 'uploading'
  | 'finishing'
  | 'analyzing'
  | 'review'
  | 'confirming'
  | 'done'
  | 'error';

const LOOSE = '__loose__';
const NEW = '__new__';

interface Item {
  id: string;
  name: string;
  state: ItemState;
  pct: number;
  message: string;
  plan?: PresignResponse;
  file: File;
  /** the take the Worker minted (present once it's landed + playable) */
  takeId?: string | null;
  analysis?: AnalyzeResult;
  /* the review form's state */
  chosen: string; // '' until picked; a song id | NEW | LOOSE
  newTitle: string;
  star: boolean;
  saveLyrics: boolean;
  lyricsText: string;
  /** true once we've confirmed the chosen song already has a locked/existing master */
  songHasMaster: boolean;
}

const REC_MIME_CANDIDATES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg'];

function pickRecMime(): string {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return '';
  for (const m of REC_MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      /* isTypeSupported can throw on a malformed string on some engines */
    }
  }
  return '';
}

function extForMime(mime: string): string {
  const m = mime.toLowerCase();
  if (m.includes('webm')) return 'webm';
  if (m.includes('ogg')) return 'ogg';
  return 'm4a';
}

function canRecord(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

export function mountUpload(host: HTMLElement, onDone: () => void): Screen {
  const items: Item[] = [];
  const plans = new Map<string, PresignResponse>();
  let songs: Song[] = [];

  const abort = new AbortController();

  /* --- record-now state. All torn down on stop AND on unmount. ------------- */
  let dead = false;
  let stream: MediaStream | null = null;
  let recorder: MediaRecorder | null = null;
  let chunks: BlobPart[] = [];
  let audioCtx: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let freqData: Uint8Array<ArrayBuffer> | null = null;
  let rafId = 0;
  let recTimerId = 0;
  let recStartedAt = 0;
  let recMime = '';
  const recDurations = new WeakMap<File, number>();

  /* ------------------------------------------------------------------ uppy */

  const uppy = new Uppy<Meta, Body>({ autoProceed: false, allowMultipleUploadBatches: true });

  const planFor = (file: UppyFile<Meta, Body>): PresignResponse => {
    const p = plans.get(file.id);
    if (!p) throw new Error('no plan');
    return p;
  };

  uppy.use(AwsS3<Meta, Body>, {
    retryDelays: [0, 1000, 3000, 6000, 12000],
    limit: 2,
    shouldUseMultipart: (file) => planFor(file).mode === 'multipart',
    getUploadParameters: (file) => {
      const plan = planFor(file);
      if (plan.mode !== 'single') throw new Error('wrong mode');
      return {
        method: 'PUT' as const,
        url: plan.url,
        headers: { 'content-type': file.type || 'application/octet-stream' },
      };
    },
    createMultipartUpload: (file) => {
      const plan = planFor(file);
      if (plan.mode !== 'multipart') throw new Error('wrong mode');
      return { uploadId: plan.upload_id, key: plan.r2_key };
    },
    getChunkSize: (file) => {
      const plan = plans.get((file as unknown as { id: string }).id);
      if (plan && plan.mode === 'multipart') return plan.part_size;
      return 8 * 1024 * 1024;
    },
    signPart: (file, { partNumber }) => {
      const plan = planFor(file);
      if (plan.mode !== 'multipart') throw new Error('wrong mode');
      const part = plan.parts.find((p) => p.part === partNumber);
      if (!part) throw new Error('no url for part');
      return { url: part.url };
    },
    listParts: () => [],
    completeMultipartUpload: async (file, { parts }) => {
      const it = items.find((i) => i.id === file.id);
      const plan = planFor(file);
      const res = await completeUpload(
        plan.upload_id,
        parts.map((p) => ({ part: p.PartNumber!, etag: p.ETag! })),
        it ? recDurations.get(it.file) ?? null : null,
      );
      if (it) it.takeId = res.take_id ?? null;
      return {};
    },
    abortMultipartUpload: () => {},
  });

  uppy.on('upload-progress', (file, progress) => {
    if (!file) return;
    const it = items.find((i) => i.id === file.id);
    if (!it) return;
    const total = progress.bytesTotal ?? it.file.size;
    it.pct = total ? Math.round(((progress.bytesUploaded ?? 0) / total) * 100) : 0;
    it.state = 'uploading';
    it.message = `${it.pct}% up`;
    paint(it);
  });

  uppy.on('upload-success', (file) => {
    if (!file) return;
    const it = items.find((i) => i.id === file.id);
    if (!it) return;
    const plan = plans.get(file.id);
    if (!plan) return;

    if (plan.mode === 'single') {
      it.state = 'finishing';
      it.message = 'Filing it…';
      paint(it);
      completeUpload(plan.upload_id, [], recDurations.get(it.file) ?? null)
        .then((res) => {
          it.takeId = res.take_id ?? null;
          void afterLanded(it);
        })
        .catch((e) => fail(it, e));
    } else {
      // multipart already ran completeUpload (and set it.takeId) above
      void afterLanded(it);
    }
  });

  uppy.on('upload-error', (file, error) => {
    if (!file) return;
    const it = items.find((i) => i.id === file.id);
    if (it) fail(it, error);
  });

  /** It's in R2 and (if playable) has a take. Read it, guess the song, and hand
   *  over to the confirm step. */
  async function afterLanded(it: Item): Promise<void> {
    if (!it.takeId) {
      // Not instantly playable (a rare format) — the source file is stored and
      // the Roadie will process it, but there's nothing to file yet.
      it.state = 'done';
      it.message = 'Uploaded. It needs processing before it can be filed.';
      paint(it);
      return;
    }
    it.state = 'analyzing';
    it.message = 'Listening — reading the words and working out the song…';
    paint(it);
    try {
      it.analysis = await analyzeTake(it.takeId);
    } catch {
      // Analysis is a nicety; if it fails, still let them file the take.
      it.analysis = {
        take_id: it.takeId,
        transcribed: false,
        failed: true,
        current_song_id: null,
        transcript: '',
        lines: [],
        guessed: null,
      };
    }
    // Pre-fill the review form from what we found.
    const a = it.analysis;
    it.chosen = a.guessed ? a.guessed.song_id : '';
    it.lyricsText = a.transcript ?? '';
    it.star = false;
    it.saveLyrics = false;
    it.songHasMaster = false;
    it.state = 'review';
    paint(it);
    // Know whether the guessed song already has words (so we don't offer to
    // clobber a master and instead defer to normal alignment/vetting).
    if (a.guessed) void checkMaster(it, a.guessed.song_id);
  }

  async function checkMaster(it: Item, songId: string): Promise<void> {
    try {
      const m = await getSongLyrics(songId, abort.signal);
      it.songHasMaster = !!(m.text && m.text.trim());
      it.saveLyrics = !it.songHasMaster && (it.analysis?.transcribed ?? false); // default-on only when there's a gap to fill
      if (it.state === 'review') paint(it);
    } catch {
      /* fine — assume no master */
    }
  }

  function fail(it: Item, e: unknown): void {
    it.state = 'error';
    it.message =
      e instanceof ApiError
        ? e.human
        : navigator.onLine
          ? 'Couldn’t reach the vault. Try again?'
          : "You're offline. It'll need a signal.";
    paint(it);
  }

  /* --------------------------------------------------------------- picking */

  async function accept(files: FileList | File[]): Promise<void> {
    const list = Array.from(files);
    if (!list.length) return;

    for (const file of list) {
      items.push({
        id: crypto.randomUUID(),
        name: file.name,
        state: 'checking',
        pct: 0,
        message: 'Asking if there’s room…',
        file,
        chosen: '',
        newTitle: '',
        star: false,
        saveLyrics: false,
        lyricsText: '',
        songHasMaster: false,
      });
    }
    render();

    for (const it of items.filter((i) => i.state === 'checking' && !i.plan)) {
      await start(it);
    }
    if (items.some((i) => i.state === 'uploading' || i.state === 'checking')) {
      uppy.upload().catch(() => undefined);
    }
  }

  async function start(it: Item): Promise<void> {
    try {
      // The gate. Song is filed in the confirm step now, so nothing to attach here.
      const plan = await presignUpload(
        {
          filename: it.file.name,
          bytes: it.file.size,
          mime: it.file.type || 'audio/mpeg',
          song_id: null,
          variant: null,
          session_hint: new Date().toISOString().slice(0, 10),
        },
        abort.signal,
      );
      it.plan = plan;
      const uppyId = uppy.addFile({ name: it.file.name, type: it.file.type, data: it.file });
      plans.set(uppyId, plan);
      it.id = uppyId;
      it.state = 'uploading';
      it.pct = 0;
      it.message = 'Starting…';
      paint(it);
    } catch (e) {
      fail(it, e);
    }
  }

  /* -------------------------------------------------------- confirm / cancel */

  async function confirmItem(it: Item): Promise<void> {
    const chosen = it.chosen;
    if (chosen === '') {
      toast('Pick a song, or choose “leave it loose”.', true);
      return;
    }
    if (chosen === NEW && !it.newTitle.trim()) {
      toast('Give the new song a name.', true);
      return;
    }

    it.state = 'confirming';
    it.message = 'Saving…';
    paint(it);

    try {
      let songId = '';
      let songTitle = '';
      if (chosen === NEW) {
        const s = await createSong(it.newTitle.trim());
        songId = s.id;
        songTitle = s.title;
      } else if (chosen !== LOOSE) {
        songId = chosen;
        songTitle = songs.find((s) => s.id === chosen)?.title ?? 'the song';
      }

      if (songId && it.takeId) {
        const { version_id } = await confirmTake(it.takeId, songId);
        if (it.star && version_id) {
          try {
            await setCanonical(songId, version_id);
          } catch {
            /* starring is a nicety; filing already worked */
          }
        }
        if (it.saveLyrics && it.lyricsText.trim()) {
          try {
            await putSongLyrics(songId, it.lyricsText.trim());
          } catch (e) {
            toast(e instanceof ApiError ? e.human : 'Filed — but the lyrics didn’t save.', true);
          }
        }
      }

      it.state = 'done';
      it.message = songId
        ? `Filed to ${songTitle}. The band can hear it.`
        : "Left loose — it's in “Just recorded”.";
      paint(it);
    } catch (e) {
      it.state = 'review';
      paint(it);
      toast(e instanceof ApiError ? e.human : "Couldn’t save that. Try again?", true);
    }
  }

  async function cancelItem(it: Item): Promise<void> {
    if (it.takeId) {
      try {
        await scrapTake(it.takeId);
      } catch {
        /* best effort — the nightly reaper mops up anything left */
      }
    }
    const i = items.indexOf(it);
    if (i >= 0) items.splice(i, 1);
    render();
  }

  /* ---------------------------------------------------------------- render */

  const rows = new Map<string, HTMLElement>();
  const listEl = h('ul', { class: 'uploads' });

  const fileInput = h('input', {
    type: 'file',
    accept: 'audio/*',
    capture: 'true',
    multiple: true,
    'aria-label': 'Choose or record audio',
  }) as HTMLInputElement;
  fileInput.setAttribute('capture', '');
  fileInput.addEventListener('change', () => {
    if (fileInput.files?.length) void accept(fileInput.files);
    fileInput.value = '';
  });

  const zone = h('div', { class: 'dropzone' }, [
    h('div', { class: 'plus', 'aria-hidden': 'true', text: '+' }),
    h('h2', { text: 'Or upload a file' }),
    h('p', { text: 'Pick a voice memo off your phone, or drop an audio file here.' }),
    fileInput,
  ]);
  zone.addEventListener('dragover', (e) => {
    e.preventDefault();
    zone.dataset.over = '1';
  });
  zone.addEventListener('dragleave', () => delete zone.dataset.over);
  zone.addEventListener('drop', (e) => {
    e.preventDefault();
    delete zone.dataset.over;
    if (e.dataTransfer?.files.length) void accept(e.dataTransfer.files);
  });

  /* ------------------------------------------------------------ record now */

  const recDot = h('span', { class: 'rec-dot', 'aria-hidden': 'true' });
  const recTime = h('span', { class: 'rec-time', text: '0:00' });
  const recStatus = h('div', { class: 'rec-status' }, [recDot, recTime]);
  const meterBars = Array.from({ length: 14 }, () => h('i', { class: 'rec-bar' }));
  const recMeter = h('div', { class: 'rec-meter', 'aria-hidden': 'true' }, meterBars);
  const recBtn = h('button', { class: 'rec-btn', type: 'button', 'aria-label': 'Record now' }, [
    h('span', { class: 'rec-btn-face', 'aria-hidden': 'true' }),
  ]);
  recBtn.addEventListener('click', () => {
    if (recorder) stopRec();
    else void startRec();
  });
  const recHint = h('div', { class: 'rec-hint', text: 'Record now' });
  const recCard = h('div', { class: 'rec', 'data-recording': '0' }, [recStatus, recMeter, recBtn, recHint]);
  recCard.hidden = !canRecord();

  async function startRec(): Promise<void> {
    if (recorder || dead) return;
    if (!canRecord()) {
      toast('This phone can’t record in the browser. Pick a voice memo instead.', true);
      return;
    }
    let s: MediaStream;
    try {
      s = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, autoGainControl: false, noiseSuppression: false, channelCount: 2, sampleRate: 48000 },
      });
    } catch {
      toast('Couldn’t open the mic. Check the app’s allowed to use it.', true);
      return;
    }
    if (dead) {
      for (const t of s.getTracks()) {
        try {
          t.stop();
        } catch {
          /* nothing to do */
        }
      }
      return;
    }
    stream = s;
    try {
      s.getAudioTracks()[0]?.getSettings?.();
    } catch {
      /* old engine */
    }
    recMime = pickRecMime();
    chunks = [];
    try {
      recorder = recMime ? new MediaRecorder(s, { mimeType: recMime }) : new MediaRecorder(s);
    } catch {
      try {
        recorder = new MediaRecorder(s);
      } catch {
        toast('Couldn’t start recording on this phone.', true);
        cleanupStream();
        return;
      }
    }
    recorder.addEventListener('dataavailable', (e) => {
      const d = (e as BlobEvent).data;
      if (d && d.size) chunks.push(d);
    });
    recorder.addEventListener('stop', () => finishRec());
    try {
      recorder.start();
    } catch {
      toast('Couldn’t start recording on this phone.', true);
      recorder = null;
      cleanupStream();
      return;
    }
    recStartedAt = Date.now();
    startMeter(s);
    recTimerId = window.setInterval(paintTimer, 250);
    paintRec();
  }

  function stopRec(): void {
    const r = recorder;
    if (!r) return;
    try {
      r.stop();
    } catch {
      finishRec();
    }
  }

  function finishRec(): void {
    const r = recorder;
    recorder = null;
    window.clearInterval(recTimerId);
    recTimerId = 0;
    const type = r?.mimeType || recMime || 'audio/mp4';
    const parts = chunks;
    chunks = [];
    cleanupStream();
    closeAudio();
    if (!dead) paintRec();
    if (dead || (!r && parts.length === 0)) return;
    const blob = new Blob(parts, { type });
    if (!blob.size) {
      toast('That came out empty — try again?', true);
      return;
    }
    const stamp = new Date().toISOString().slice(0, 10);
    const file = new File([blob], `jam-${stamp}.${extForMime(type)}`, { type });
    const durS = recStartedAt ? Math.round((Date.now() - recStartedAt) / 1000) : 0;
    if (durS > 0) recDurations.set(file, durS);
    toast('Got it — reading it now…');
    void accept([file]);
  }

  function startMeter(s: MediaStream): void {
    try {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      audioCtx = new Ctx();
      void audioCtx.resume?.();
      const src = audioCtx.createMediaStreamSource(s);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 64;
      src.connect(analyser);
      freqData = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
      const tick = (): void => {
        if (!analyser || !freqData) return;
        analyser.getByteFrequencyData(freqData);
        paintMeter();
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    } catch {
      /* the meter is a flourish */
    }
  }

  function paintMeter(): void {
    if (!freqData) return;
    for (let i = 0; i < meterBars.length; i++) {
      const bin = freqData[Math.min(freqData.length - 1, i + 1)] ?? 0;
      meterBars[i]!.style.transform = `scaleY(${(0.1 + (bin / 255) * 0.9).toFixed(3)})`;
    }
  }
  function resetMeter(): void {
    for (const b of meterBars) b.style.transform = 'scaleY(0.1)';
  }
  function paintTimer(): void {
    recTime.textContent = fmtTime(recStartedAt ? Math.floor((Date.now() - recStartedAt) / 1000) : 0);
  }
  function paintRec(): void {
    const on = !!recorder;
    recCard.dataset.recording = on ? '1' : '0';
    recBtn.setAttribute('aria-label', on ? 'Stop recording' : 'Record now');
    recBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    recHint.textContent = on ? 'Recording — tap to stop' : 'Record now';
    if (!on) {
      recTime.textContent = '0:00';
      resetMeter();
    }
  }
  function cleanupStream(): void {
    if (stream) {
      for (const t of stream.getTracks()) {
        try {
          t.stop();
        } catch {
          /* can't stop — nothing to do */
        }
      }
      stream = null;
    }
  }
  function closeAudio(): void {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
    if (audioCtx) {
      try {
        void audioCtx.close();
      } catch {
        /* already closed */
      }
      audioCtx = null;
    }
    analyser = null;
    freqData = null;
  }

  /* --------------------------------------------------------------- rows */

  function render(): void {
    clear(listEl);
    rows.clear();
    for (const it of items) {
      const row = rowFor(it);
      rows.set(it.id, row);
      listEl.appendChild(row);
    }
    listEl.hidden = items.length === 0;
    syncDoneBtn();
  }

  function syncDoneBtn(): void {
    const nDone = items.filter((i) => i.state === 'done').length;
    const anyOpen = items.some((i) => i.state !== 'done' && i.state !== 'error');
    doneBtn.hidden = nDone === 0 && !anyOpen;
    doneBtn.textContent = nDone > 0 ? `Done — back to the shelf${nDone > 1 ? ` (${nDone})` : ''}` : 'Back to the shelf';
  }

  function rowFor(it: Item): HTMLElement {
    if (it.state === 'review') return reviewCard(it);

    const bar = h('div', { class: 'bar' }, [h('i')]);
    (bar.firstChild as HTMLElement).style.width = `${it.pct}%`;
    const spin = it.state === 'analyzing';

    const row = h('li', { class: 'up card', 'data-state': it.state }, [
      h('div', { class: 'up-head' }, [
        h('span', { class: 'up-name', text: it.name }),
        h('span', { class: 'up-pct', text: it.state === 'done' ? '✓' : spin ? '' : `${it.pct}%` }),
      ]),
      spin ? h('div', { class: 'up-spinner', 'aria-hidden': 'true' }) : bar,
      h('p', { class: 'up-msg', text: it.message }),
    ]);

    if (it.state === 'error') {
      const retry = h('button', { class: 'btn-sm primary', text: 'Try again' });
      retry.addEventListener('click', () => {
        it.state = 'checking';
        it.pct = 0;
        it.message = 'Asking if there’s room…';
        it.plan = undefined;
        paint(it);
        void start(it).then(() => uppy.upload().catch(() => undefined));
      });
      row.appendChild(h('div', { class: 'up-actions' }, [retry]));
    }
    return row;
  }

  /** The confirm step. Built once per entry into 'review' and NOT repainted while
   *  the user types (that would drop focus) — its fields write straight to `it`. */
  function reviewCard(it: Item): HTMLElement {
    const a = it.analysis;

    // --- song picker
    const select = h('select', { class: 'rv-song', 'aria-label': 'Which song is this?' }) as HTMLSelectElement;
    const opt = (v: string, t: string) => h('option', { value: v, text: t }) as HTMLOptionElement;
    if (a?.guessed) select.appendChild(opt(a.guessed.song_id, `${a.guessed.title}  ✓ our guess`));
    for (const s of songs) {
      if (a?.guessed && s.id === a.guessed.song_id) continue;
      select.appendChild(opt(s.id, s.title));
    }
    select.appendChild(opt(NEW, '＋ New song…'));
    select.appendChild(opt(LOOSE, "Not sure — leave it loose"));
    select.value = it.chosen || (a?.guessed ? a.guessed.song_id : LOOSE);
    it.chosen = select.value;

    // --- new-song title (hidden unless NEW)
    const newTitle = h('input', {
      class: 'rv-newtitle',
      type: 'text',
      placeholder: 'New song title',
      maxlength: 120,
    }) as HTMLInputElement;
    const newWrap = h('div', { class: 'rv-field', hidden: it.chosen !== NEW }, [newTitle]);
    newTitle.addEventListener('input', () => (it.newTitle = newTitle.value));

    // --- star
    const star = h('input', { class: 'rv-check', type: 'checkbox', id: `star-${it.id}` }) as HTMLInputElement;
    star.checked = it.star;
    star.addEventListener('change', () => (it.star = star.checked));
    const starRow = h('label', { class: 'rv-toggle', for: `star-${it.id}` }, [
      star,
      h('span', {}, [h('b', { text: '★ Make this the main take' }), h('small', { text: 'the one on the shelf' })]),
    ]);

    // --- lyrics
    const lyrHead = h('div', { class: 'rv-lyrhead' });
    const lyrArea = h('textarea', { class: 'rv-lyrics', rows: '6', placeholder: 'The words we heard…' }) as HTMLTextAreaElement;
    lyrArea.value = it.lyricsText;
    lyrArea.addEventListener('input', () => (it.lyricsText = lyrArea.value));
    const saveChk = h('input', { class: 'rv-check', type: 'checkbox', id: `lyr-${it.id}` }) as HTMLInputElement;
    saveChk.checked = it.saveLyrics;
    saveChk.addEventListener('change', () => (it.saveLyrics = saveChk.checked));
    const saveRow = h('label', { class: 'rv-toggle', for: `lyr-${it.id}` }, [
      saveChk,
      h('span', {}, [h('b', { text: 'Save these as the song’s lyrics' }), h('small', { class: 'rv-lyrnote', text: '' })]),
    ]);
    const lyrWrap = h('div', { class: 'rv-lyrics-wrap' }, [lyrHead, lyrArea, saveRow]);

    function paintLyricState(): void {
      const transcribed = a?.transcribed ?? false;
      if (!transcribed) {
        lyrHead.textContent = a?.too_big
          ? 'Too big to auto-read the words — it still plays. Upload a compressed (mp3/m4a) copy for lyrics.'
          : 'Couldn’t make out the words on this one.';
        lyrWrap.dataset.empty = '1';
        lyrArea.hidden = true;
        saveRow.hidden = true;
        return;
      }
      lyrWrap.dataset.empty = '0';
      lyrArea.hidden = false;
      const filedToSong = it.chosen && it.chosen !== LOOSE;
      saveRow.hidden = !filedToSong;
      const note = saveRow.querySelector('.rv-lyrnote') as HTMLElement | null;
      if (it.songHasMaster) {
        if (note) note.textContent = 'heads up: this song already has lyrics — saving replaces them';
        lyrHead.textContent = 'What we heard (edit if it’s off):';
      } else {
        if (note) note.textContent = 'gives the song its first set of words';
        lyrHead.textContent = 'What we heard (edit if it’s off):';
      }
    }

    select.addEventListener('change', () => {
      it.chosen = select.value;
      newWrap.hidden = it.chosen !== NEW;
      const filed = it.chosen !== LOOSE;
      starRow.hidden = !filed;
      it.songHasMaster = false;
      if (it.chosen && it.chosen !== LOOSE && it.chosen !== NEW) void checkMasterInline(it.chosen);
      else paintLyricState();
    });

    async function checkMasterInline(songId: string): Promise<void> {
      try {
        const m = await getSongLyrics(songId, abort.signal);
        it.songHasMaster = !!(m.text && m.text.trim());
      } catch {
        it.songHasMaster = false;
      }
      it.saveLyrics = !it.songHasMaster && (a?.transcribed ?? false);
      saveChk.checked = it.saveLyrics;
      paintLyricState();
    }

    starRow.hidden = it.chosen === LOOSE;
    paintLyricState();

    // --- actions
    const confirm = h('button', { class: 'btn-sm primary rv-confirm', text: 'Confirm' });
    confirm.addEventListener('click', () => void confirmItem(it));
    const cancel = h('button', { class: 'btn-sm rv-cancel', text: 'Cancel' });
    cancel.addEventListener('click', () => void cancelItem(it));

    const guessLine = a?.guessed
      ? h('p', { class: 'rv-guess', text: `Sounds like “${a.guessed.title}”.` })
      : a?.transcribed
        ? h('p', { class: 'rv-guess rv-guess--none', text: 'Couldn’t tell which song — pick one below.' })
        : null;

    return h('li', { class: 'up card rv', 'data-state': 'review' }, [
      h('div', { class: 'up-head' }, [
        h('span', { class: 'up-name', text: it.name }),
        h('span', { class: 'rv-badge', text: 'confirm' }),
      ]),
      guessLine,
      h('div', { class: 'rv-field' }, [h('label', { class: 'rv-label', text: 'Which song is this?' }), select, newWrap]),
      lyrWrap,
      starRow,
      h('div', { class: 'rv-actions' }, [cancel, confirm]),
    ]);
  }

  function paint(it: Item): void {
    const old = rows.get(it.id);
    const next = rowFor(it);
    if (old) {
      old.replaceWith(next);
      rows.set(it.id, next);
    } else {
      render();
      return;
    }
    syncDoneBtn();
  }

  const doneBtn = h('button', { class: 'btn-play', text: 'Back to the shelf' });
  doneBtn.hidden = true;
  doneBtn.addEventListener('click', () => onDone());

  const top = h('header', { class: 'topbar' }, [
    h('div', {}, [h('h1', { text: 'Add' }), h('div', { class: 'sub', text: 'to the vault' })]),
  ]);

  host.append(
    top,
    h('div', { class: 'upload-screen' }, [
      recCard,
      zone,
      listEl,
      doneBtn,
      h('p', {
        class: 'wave-hint',
        text: 'It lands, we read the words and guess the song, then you confirm. Nothing goes live until you do.',
      }),
    ]),
  );

  render();

  getSongs(abort.signal)
    .then((s) => {
      songs = s;
    })
    .catch(() => {
      /* no song list is fine — the review still offers new/loose */
    });

  return {
    destroy() {
      dead = true;
      abort.abort();
      const r = recorder;
      recorder = null;
      if (r) {
        try {
          r.stop();
        } catch {
          /* already stopped */
        }
      }
      window.clearInterval(recTimerId);
      cleanupStream();
      closeAudio();
      uppy.destroy();
      plans.clear();
    },
  };
}
